import type { CSSProperties } from 'react';
import { PROFICIENCY_LEVEL_LABELS } from '@/components/custom/proficiency-scale-reference';
import { StickyTableScroll } from '@/components/custom/sticky-table-scroll';
import { formatHours } from '@/components/custom/timesheet-week';
import {
  Table,
  TableBody,
  TableCell,
  TableHead,
  TableHeader,
  TableRow,
} from '@/components/ui/table';
import { cn } from '@/lib/utils';
import type { SkillMatrixGrid as Grid } from './skill-matrix-grid-data';

interface Props {
  grid: Grid;
}

/**
 * Employee and Location are each fixed-width — not auto-sized like every
 * other column — because a sticky cell's `left` offset has to be a known
 * number: `position: sticky` can't ask "wherever the column before me
 * landed", so each one's pixel width is declared here and the next one's
 * `left` is the running sum. A metric column (Available Hours today; more
 * if that ever grows) gets the same treatment at whatever index it falls
 * at, computed from `METRIC_WIDTH` rather than hardcoded, since there's no
 * fixed count of them.
 *
 * Employee carries the email too, as a second line under the name (not its
 * own column) — wider than a bare name needs, since that line is usually
 * the longer of the two.
 */
const EMPLOYEE_WIDTH = 220;
const LOCATION_WIDTH = 140;
const METRIC_WIDTH = 130;
const EMPLOYEE_LEFT = 0;
const LOCATION_LEFT = EMPLOYEE_LEFT + EMPLOYEE_WIDTH;
const METRICS_LEFT = LOCATION_LEFT + LOCATION_WIDTH;

const EM_DASH = <span className="text-muted-foreground">—</span>;

/** Pixel position for a frozen column — `position: sticky` needs the exact
 *  offset, not "after whichever column came before". */
function frozenStyle(left: number, width: number): CSSProperties {
  return { position: 'sticky', left, width, minWidth: width, maxWidth: width };
}

/**
 * A frozen cell's background, stacking order and (for the last one) the
 * freeze boundary — everything but its position and its own text layout,
 * which differ (Employee is two lines; everything else is one).
 *
 * `isLast` draws that boundary — one divider after the whole frozen block
 * reads cleaner than a border between each of its own columns.
 *
 * The header's frozen cells need `!z-20` — `StickyTableScroll` already pins
 * every `thead th` to `top-0 z-10` via its own class list (the vertical
 * freeze every report table has), and without `!important` the two utility
 * classes setting `z-index` on the SAME cell — its own left-freeze and that
 * blanket top-freeze — would race on generation order instead of composing
 * predictably. A body cell carries only one sticky rule, so `z-10` alone is
 * enough there.
 */
function frozenClass(header: boolean, isLast: boolean): string {
  // `bg-card`, not `bg-background`: the table itself is a white card on the
  // cream page (`StickyTableScroll`'s own convention — see its footer row),
  // and a frozen cell still needs to be OPAQUE so a scrolled skill column
  // can't bleed through underneath it. `bg-background` painted it the
  // page's cream instead of the table's white, which read as a different
  // colour from the skill cells beside it.
  return cn(header ? '!z-20 bg-surface-subtle' : 'z-10 bg-card', isLast && 'border-r');
}

/**
 * Pure render over an already-run grid — narrowing who and which skills
 * show is the Skill Matrix page's criteria screen's job (filters bound into
 * the query itself, same as the Timesheet report), not this component's.
 *
 * One ROW per employee, one COLUMN per metric/skill — an ordinary data
 * table, each employee read across a line rather than down one. Employee,
 * Location and the metric columns stay frozen on the left while the skill
 * columns scroll underneath them.
 */
export function SkillMatrixGridView({ grid }: Props) {
  if (grid.employees.length === 0) {
    return (
      <p className="py-10 text-center text-muted-foreground text-sm">
        No employees match these criteria.
      </p>
    );
  }

  const lastMetricIndex = grid.metricRows.length - 1;

  return (
    <>
      <StickyTableScroll>
        <Table className="text-sm">
          <TableHeader>
            <TableRow>
              <TableHead
                style={frozenStyle(EMPLOYEE_LEFT, EMPLOYEE_WIDTH)}
                className={frozenClass(true, false)}
              >
                Employee
              </TableHead>
              <TableHead
                style={frozenStyle(LOCATION_LEFT, LOCATION_WIDTH)}
                className={cn(frozenClass(true, lastMetricIndex < 0), 'truncate')}
              >
                Location
              </TableHead>
              {grid.metricRows.map((metric, i) => (
                <TableHead
                  key={metric.key}
                  style={frozenStyle(METRICS_LEFT + i * METRIC_WIDTH, METRIC_WIDTH)}
                  className={cn(frozenClass(true, i === lastMetricIndex), 'truncate text-right')}
                >
                  {metric.label}
                </TableHead>
              ))}
              {grid.skillRows.map((skill) => (
                <TableHead key={skill.key} className="text-right">
                  {skill.label}
                </TableHead>
              ))}
            </TableRow>
          </TableHeader>
          <TableBody>
            {grid.employees.map((employee) => {
              const email = grid.emailByEmployee[employee.key];
              const location = grid.locationByEmployee[employee.key];
              return (
                <TableRow key={employee.key}>
                  <TableCell
                    style={frozenStyle(EMPLOYEE_LEFT, EMPLOYEE_WIDTH)}
                    className={frozenClass(false, false)}
                  >
                    <div className="truncate font-medium">{employee.label}</div>
                    {email ? (
                      <div className="truncate text-xs text-muted-foreground">{email}</div>
                    ) : null}
                  </TableCell>
                  <TableCell
                    style={frozenStyle(LOCATION_LEFT, LOCATION_WIDTH)}
                    className={cn(frozenClass(false, lastMetricIndex < 0), 'truncate')}
                  >
                    {location ? location : EM_DASH}
                  </TableCell>
                  {grid.metricRows.map((metric, i) => {
                    const hours = metric.values[employee.key];
                    return (
                      <TableCell
                        key={metric.key}
                        style={frozenStyle(METRICS_LEFT + i * METRIC_WIDTH, METRIC_WIDTH)}
                        className={cn(
                          frozenClass(false, i === lastMetricIndex),
                          'truncate text-right tabular-nums',
                        )}
                      >
                        {hours == null ? EM_DASH : formatHours(hours)}
                      </TableCell>
                    );
                  })}
                  {grid.skillRows.map((skill) => {
                    const level = skill.values[employee.key];
                    const label =
                      level == null
                        ? null
                        : `${level} - ${PROFICIENCY_LEVEL_LABELS[skill.skillType][Number(level)]}`;
                    return (
                      <TableCell key={skill.key} className="text-right">
                        {label ? label : EM_DASH}
                      </TableCell>
                    );
                  })}
                </TableRow>
              );
            })}
          </TableBody>
        </Table>
      </StickyTableScroll>
      {grid.skillRows.length === 0 ? (
        <p className="text-muted-foreground text-xs">No skills rated under these criteria.</p>
      ) : null}
    </>
  );
}
